import { CampaignTableRow } from '../CampaignsTable/ReportCampaignsTable';
import { useEnsureInstagramReportCampaignPosts } from '../../Posts/InstagramReportPostsTable/useInstagramReportCampaignPosts';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import InstagramReportClipboardTable, {
    mapToClipboardReportRow as mapToIGClipboardReportRow,
    mapToClipboardReportTotals as mapToIGClipboardReportTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/InstagramReportClipboardTable';
import copyComponentRenderToClipboard from 'utility/copyToClipboard/copyComponentRenderToClipboard';
import { useEnsureTiktokReportCampaignPosts } from '../../Posts/PostsTable/useTiktokReportCampaignPosts';
import TiktokReportClipboardTable, {
    mapToClipboardReportRow as mapToTTClipboardReportRow,
    mapToClipboardReportTotals as mapToTTClipboardReportTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/TiktokReportClipboardTable';
import { useEnsureYoutubeReportCampaignPosts } from '../../Posts/YoutubeReportPostsTable/useYoutubeReportCampaignPosts';
import YoutubeReportClipboardTable, {
    mapToClipboardReportRow as mapToYTClipboardReportRow,
    mapToClipboardTableTotals as mapToYTClipboardTableTotals,
} from 'Modules/Plans/CopyToClipboard/projectReport/YoutubeReportClipboardTable';

export default function useCopyReport(campaign: CampaignTableRow) {
    const ensureTiktokPosts = useEnsureTiktokReportCampaignPosts();
    const ensureInstagramPosts = useEnsureInstagramReportCampaignPosts();
    const ensureYoutubePosts = useEnsureYoutubeReportCampaignPosts();

    const copyInstagram = async () => {
        try {
            const rows = await ensureInstagramPosts(campaign.id);
            const items = rows.map((row) =>
                mapToIGClipboardReportRow({
                    post: row,
                    user: row.user,
                    userStats: row.userStatsCache,
                })
            );

            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals: mapToIGClipboardReportTotals(campaign.stats, items),
                    items,
                    includeCost: rows.some((p) => 'cost' in p),
                },
                InstagramReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        }
    };

    const copyTiktok = async () => {
        try {
            const rows = await ensureTiktokPosts(campaign.id);
            const items = rows.map((row) =>
                mapToTTClipboardReportRow({
                    post: row as creatorbase.TiktokPost,
                    user: row.user,
                    userStats: row.userStatsCache,
                })
            );

            const totals = mapToTTClipboardReportTotals(items, campaign.stats);
            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals,
                    items,
                    includeCost: rows.some((p) => 'cost' in p),
                },
                TiktokReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        }
    };

    const copyYoutube = async () => {
        try {
            const rows = await ensureYoutubePosts(campaign.id);
            const items = rows.map((row) => mapToYTClipboardReportRow({ post: row, channel: row.channel }));

            // Add the totals values we can get from the campaign stats
            const totals = mapToYTClipboardTableTotals(campaign.stats, items);
            const success = copyComponentRenderToClipboard(
                {
                    artistName: campaign.brand?.name,
                    projectName: campaign.project?.name,
                    campaignName: campaign.name,
                    currency: campaign.currency,
                    totals,
                    items,
                    includeCost: rows.some((p) => 'cost' in p),
                },
                YoutubeReportClipboardTable
            );

            if (success) {
                showNotification('Copied!', 'info');
            } else {
                showNotification('Could not copy', 'error');
            }
        } catch {
            showNotification('Could not copy', 'error');
        }
    };

    const copyActions: { [platform in creatorbase.Platform]: () => Promise<void> } = {
        instagram: copyInstagram,
        youtube: copyYoutube,
        tiktok: copyTiktok,
    };

    return copyActions[campaign.platform];
}
